import {DumpsterFire} from "../../DumpsterFire";
import {gsap, Linear, TimelineLite, TweenLite} from "gsap";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {Sprite} from "pixi.js";
import {CustomWiggle} from "gsap/dist/CustomWiggle";
import {DumpsterFireSymbol} from "../../reels/DumpsterFireSymbol";
import {StageLayer} from "@nolimitcity/slot-keypad/bin/core/app/StageLayer";

export class DFReelAnticipation extends PIXI.Container implements StageLayer {
    public _animation: PIXI.spine.Spine;
    private _topGradients: PIXI.Sprite[];

    constructor() {
        super();
        this.name = "ReelAnticipation";
        this.init();
    }

    public resetSpineItem(item: PIXI.spine.Spine): void {
        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.clearTracks();
        item.state.setEmptyAnimation(0, 0);
    }

    public reset(value: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if ((DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1 || DumpsterFire.model.data.mode.indexOf("EXTRA_SPIN") > -1) && DumpsterFire.model.data.upgradedSymbols.length > 0) {
            return tl;
        }

        this._topGradients.forEach((gradient: Sprite) => {
            tl.add(gsap.to(gradient, {alpha: 0, duration: 0.001}), 0);
            tl.add(() => {
                gradient.visible = false;
            })
        });
        this.resetSpineItem(this._animation);
        tl.add(gsap.to(this._animation, {alpha: 0, duration: 0.001}), 0);
        this._animation.visible = false
        return tl;
    }

    public update(reelId: number): void {
        if ((DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1 || DumpsterFire.model.data.mode.indexOf("EXTRA_SPIN") > -1) && DumpsterFire.model.data.upgradedSymbols.length > 0) {
            return;
        }
        if (reelId > 5) {
            return
        }
        if (this._topGradients[reelId].visible) {
            return
        }
        if (DumpsterFire.model.nearWinReels[reelId]) {
            this._topGradients[reelId].visible = true;
            if (!this._animation.visible) {
                DumpsterFire.soundPlayer.playAnticipationSound();
                this._animation.visible = true;
                this.addChild(this._animation);
                this._animation.state.setAnimation(0, "animation", true);
                TweenLite.to(this._animation, 0.1, {alpha: 1});
            }
            this.addChild(this._topGradients[reelId]);
            TweenLite.to(this._topGradients[reelId], 0.1, {alpha: 1});
        }
    }

    public hideContainer(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if ((DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1 || DumpsterFire.model.data.mode.indexOf("EXTRA_SPIN") > -1) && DumpsterFire.model.data.upgradedSymbols.length > 0) {
            return tl;
        }
        if (DumpsterFire.reelAnticipation._animation.visible && DumpsterFire.reelAnticipation.alpha !== 0) {
            tl.add([gsap.to(this, {alpha: 0, duration: 0.1}),
                () => {
                    DumpsterFire.soundPlayer.stopAnticipationSound();
                }
            ], 0);
        }
        DumpsterFire.model.scatterSymArray.forEach((sym) => {
            tl.add(() => {
                DumpsterFire.winView.playGlow_2_Animation(<DumpsterFireSymbol>sym)
            }, 0);
        });
        return tl;
    }

    public showContainer(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if ((DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1 || DumpsterFire.model.data.mode.indexOf("EXTRA_SPIN") > -1) && DumpsterFire.model.data.upgradedSymbols.length > 0) {
            return tl;
        }
        if (this.alpha === 0) {
            tl.add(gsap.to(this, {alpha: 1, duration: 0.001}), 0);
            DumpsterFire.model.scatterSymArray.forEach((item) => {
                tl.add(() => {
                    DumpsterFire.winView.playGlowAnimation(<DumpsterFireSymbol>item)
                }, 0);
            });
        }
        return tl;
    }

    private init(): void {
        this._animation = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset("scatterAnticipation"));
        this._animation.visible = false;
        this._animation.position.set(360, 369);
        this._animation.alpha = 0;
        this.addChild(this._animation);
        this._topGradients = ArrayHelper.initArrayWithValues(6, (i: number) => {
            const gradient: Sprite = new Sprite(DumpsterFire.assetLoader.getTextures("reel_highlight")[0]);
            gradient.name = "ReelGradient";
            gradient.alpha = 0;
            gradient.visible = false;
            gradient.blendMode = PIXI.BLEND_MODES.SCREEN;
            gradient.anchor.set(0, 0);
            gradient.position.set(118 * i + 8, 68);
            return gradient;
        })
    }

    public getAnticipationShake(dur: number = 0.2, wiggles: number = 1, isVertical: boolean = true, movepos?: number): gsap.core.Timeline {
        let moveXVal: number = movepos ? movepos : 30,
            moveYVal: number = movepos ? movepos : 30;
        let wiggleTl: gsap.core.Timeline = gsap.timeline(),
            x: string = Math.random() > 0.5 ? `+=${moveXVal}` : `+=${moveXVal}`,
            y: string = Math.random() > 0.5 ? `+=${moveYVal}` : `+=${moveYVal}`;

        const shakeWiggle = CustomWiggle.create("shakeWiggle", {
            wiggles: wiggles,
            amplitudeEase: Linear.easeNone,
            timingEase: Linear.easeNone
        });
        if (!isVertical) {
            wiggleTl.to(this.position, {
                duration: dur,
                x: x,
                ease: shakeWiggle
            }, 0);
        }
        wiggleTl.to(this.position, {
            duration: dur,
            y: y,
            ease: shakeWiggle
        }, 0);
        wiggleTl.to(this.position, {
            x: 0,
            y: 0,
            duration: 0.01
        });

        return wiggleTl;
    }
}